import React, { useState, useEffect } from 'react';
import { setPageMeta } from '../utils/setPageMeta';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { CheckCircle, ArrowRight, Rocket, MessageCircle, DollarSign, Phone, Star, Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import HorizontalScrollMobile from '../components/HorizontalScrollMobile';
import { toast } from 'sonner';
import { openChat } from '../utils/chat';

const HomePage = () => {
  useEffect(() => {
    setPageMeta(
      'Legal Flick - Professional Legal Services Online',
      'Expert legal document preparation and filing services for businesses and individuals. LLC formation, trademark registration, wills, and more. Trusted by 500,000+ customers.'
    );
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('');

  // Main action cards
  const actionCards = [
    {
      title: 'LLC Formation',
      image: 'https://images.pexels.com/photos/7310202/pexels-photo-7310202.jpeg',
      link: '/services/llc',
    },
    {
      title: 'Business Compliance',
      image: 'https://images.pexels.com/photos/19825346/pexels-photo-19825346.jpeg',
      link: '/services/compliance',
    },
    {
      title: 'Trademark Registration',
      image: 'https://images.unsplash.com/photo-1625419693247-fe1712a18a46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHx0cmFkZW1hcmt8ZW58MHx8fHwxNzY5NDY5MDUxfDA&ixlib=rb-4.1.0&q=85',
      link: '/services/trademark',
    },
    {
      title: 'Last Will & Testament',
      image: 'https://images.pexels.com/photos/5257932/pexels-photo-5257932.jpeg',
      link: '/services/will',
    },
    {
      title: 'Business Attorney',
      image: 'https://images.pexels.com/photos/7841450/pexels-photo-7841450.jpeg',
      link: '/services/business-attorney',
    },
    {
      title: 'Operating Agreement',
      image: 'https://images.unsplash.com/photo-1681505531034-8d67054e07f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbnRyYWN0fGVufDB8fHx8MTc2OTQ2OTA1N3ww&ixlib=rb-4.1.0&q=85',
      link: '/services/operating-agreement',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Submit your information',
      description: 'Select the legal service you need and submit the information. Most of our questionnaires can be completed in minutes.',
      image: 'https://images.unsplash.com/photo-1508780709619-79562169bc64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx0eXBpbmclMjBjb21wdXRlcnxlbnwwfHx8fDE3NjYxNjc2Njd8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      number: '02',
      title: "We'll process your request",
      description: "Rest easy while we handle the necessary next steps. We'll ensure all requests are fulfilled and distributed accurately, working with government agencies as needed.",
      image: 'https://images.unsplash.com/photo-1551836022-4c4c79ecde51',
    },
    {
      number: '03',
      title: 'Stay up-to-date',
      description: 'Log in anytime to your personalized dashboard, a centralized hub for important updates and documents.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f',
    },
  ];

  const supportOptions = [
    {
      title: 'Self-Service Solutions',
      subtitle: "Empower yourself with our easy-to-use legal document platform.",
      image: 'https://images.unsplash.com/photo-1557186817-c11ad50d5b41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHx0eXBpbmclMjBjb21wdXRlcnxlbnwwfHx8fDE3NjYxNjc2Njd8MA&ixlib=rb-4.1.0&q=85',
      features: [
        'Step-by-step guidance through complex legal processes',
        'Instant document generation and filing assistance',
        'Secure online dashboard to track your progress anytime',
      ],
    },
    {
      title: 'Professional Legal Support',
      subtitle: 'Connect with experienced attorneys for personalized advice.',
      image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7',
      features: [
        'Network of vetted legal professionals across all specialties',
        'Comprehensive consultations tailored to your specific needs',
        'Transparent pricing with no hidden fees or surprises',
      ],
    },
  ];

  const attorneyBenefits = [
    {
      icon: Rocket,
      title: 'Easy and convenient access',
      description: 'Find an attorney who meets your needs. We make it easy to connect and keep track of your progress.',
    },
    {
      icon: MessageCircle,
      title: 'Experience you can trust',
      description: 'Get your questions answered by our highly-vetted network of attorneys.',
    },
    {
      icon: DollarSign,
      title: 'Predictable pricing',
      description: 'From business to personal legal services, you will always know the full price before you begin.',
    },
  ];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        {/* Darker Abstract Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-200">
          <div className="absolute inset-0 opacity-40">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6">
              Legal solutions trusted by thousands,
              <br />
              designed for you
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto">
              Step-by-step tools and attorney guidance for your business and personal legal needs.
            </p>
          </div>

          {/* Action Cards Grid */}
          <HorizontalScrollMobile desktopCols="md:grid-cols-3 lg:grid-cols-6" className="grid-cols-2">
            {actionCards.map((card, idx) => (
              <Link
                key={idx}
                to={card.link}
                className="group bg-white rounded-lg shadow-md hover:shadow-2xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">{card.title}</span>
                  <ArrowRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </HorizontalScrollMobile>
        </div>
      </section>

      {/* Find the support section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-16">
            Find the support that's right for you
          </h2>

          <div className="grid lg:grid-cols-2 gap-12">
            {supportOptions.map((option, idx) => (
              <Card key={idx} className="overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={option.image}
                    alt={option.title}
                    className="w-full h-full object-cover"
                  />
                  {option.attorney && (
                    <div className="absolute bottom-4 left-4 bg-white rounded-lg p-3 shadow-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-bold text-lg">
                            {option.attorney.name[0]}
                          </span>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{option.attorney.name}</div>
                          <div className="text-sm text-gray-600">{option.attorney.experience}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{option.title}</h3>
                  <p className="text-gray-600 mb-6">{option.subtitle}</p>
                  <ul className="space-y-3">
                    {option.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Talk to specialist */}
          <Card className="mt-12 bg-gradient-to-br from-gray-900 to-black border-gray-800 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Talk to a specialist</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Connect with a business or personal services specialist in under two minutes to help get you to the solutions you need.
              </p>
              <a
                href="tel:+18555484511"
                className="inline-flex items-center text-blue-400 hover:text-blue-300 text-xl font-semibold"
              >
                <Phone className="w-5 h-5 mr-2" />
                +1 (855) 548-4511
              </a>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How it works section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">How it works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We make navigating legal processes easier, whether you want to use our guided tools or choose an attorney to help you.
            </p>
          </div>

          <HorizontalScrollMobile desktopCols="md:grid-cols-3">
            {steps.map((step, idx) => (
              <div key={idx} className="relative">
                <Card className="h-full hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="text-6xl font-bold text-blue-100 mb-4">{step.number}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </HorizontalScrollMobile>
        </div>
      </section>

      {/* Get personalized attorney guidance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Get personalized attorney guidance
            </h2>
            <p className="text-xl text-gray-600">
              Let our network of attorneys save you time, money, and headaches.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="relative rounded-lg overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxhdHRvcm5leSUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3NjYxNjU2MTd8MA&ixlib=rb-4.1.0&q=85"
                alt="Attorney Consultation Meeting"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="relative rounded-lg overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1507842217343-583bb7270b66"
                alt="Legal Books Collection"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>

          <HorizontalScrollMobile desktopCols="md:grid-cols-3" className="mb-12">
            {attorneyBenefits.map((benefit, idx) => (
              <Card key={idx} className="text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <CardContent className="p-8">
                  <benefit.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </HorizontalScrollMobile>

          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Connect with an attorney to discuss your legal needs
            </h3>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setIsModalOpen(true)}
              >
                Get Started
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={openChat}
              >
                  Chat Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Discover why thousands of people choose Legal Flick
            </h2>
            <p className="text-xl text-blue-100">Real stories from satisfied customers</p>
          </div>

          <div className="relative">
            <div className="flex gap-6 animate-scroll-testimonials">
              {[
                {
                  content: "It amazes me something so important was so easy to do... everything was exactly as I had stated. I have peace of mind now.",
                  name: "Jennifer Williams",
                  service: "Last Will & Testament",
                },
                {
                  content: "Legal Flick made forming my LLC incredibly straightforward. The whole process was smooth and professional from start to finish.",
                  name: "Michael Thompson",
                  service: "LLC Formation",
                },
                {
                  content: "Excellent trademark service. They handled everything efficiently and kept me informed throughout the entire registration process.",
                  name: "Sarah Martinez",
                  service: "Trademark Registration",
                },
                {
                  content: "The registered agent service is outstanding. They're always professional and ensure I never miss important legal documents.",
                  name: "David Chen",
                  service: "Registered Agent",
                },
                {
                  content: "Creating my living trust was easier than I expected. The platform is user-friendly and the support team was incredibly helpful.",
                  name: "Patricia Anderson",
                  service: "Living Trust",
                },
                {
                  content: "Fast, reliable, and affordable. Legal Flick handled all the paperwork for my corporation formation flawlessly.",
                  name: "Robert Foster",
                  service: "Corporation Formation",
                },
                {
                  content: "The business license research report saved me so much time. They identified all the licenses I needed for my specific business.",
                  name: "Amanda Rodriguez",
                  service: "Business Licenses",
                },
                {
                  content: "Professional estate planning services at a fraction of traditional costs. I'm confident my family is protected.",
                  name: "James Patterson",
                  service: "Estate Planning",
                },
              ].concat([
                {
                  content: "It amazes me something so important was so easy to do... everything was exactly as I had stated. I have peace of mind now.",
                  name: "Jennifer Williams",
                  service: "Last Will & Testament",
                },
                {
                  content: "Legal Flick made forming my LLC incredibly straightforward. The whole process was smooth and professional from start to finish.",
                  name: "Michael Thompson",
                  service: "LLC Formation",
                },
                {
                  content: "Excellent trademark service. They handled everything efficiently and kept me informed throughout the entire registration process.",
                  name: "Sarah Martinez",
                  service: "Trademark Registration",
                },
              ]).map((testimonial, idx) => (
                <Card key={idx} className="flex-shrink-0 w-96 bg-white/10 backdrop-blur-sm border-white/20">
                  <CardContent className="p-6 text-center">
                    <div className="flex justify-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-lg text-white mb-4 italic min-h-[100px]">
                      &ldquo;{testimonial.content}&rdquo;
                    </p>
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-blue-100 text-sm">{testimonial.service}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1554774853-aae0a22c8aa4"
                alt="Business owner"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div>
              <div className="text-6xl font-bold text-blue-600 mb-4">100%</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Accurate Filing Guarantee</h3>
              <p className="text-gray-600 text-lg">
                We're committed to the highest quality and accuracy. If your filing is rejected or incorrect due to our error, we'll correct it with the government agency at no additional cost to you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Popular legal services
            </h2>
            <p className="text-lg text-gray-600">
              Get started with our most requested services
            </p>
          </div>

          <HorizontalScrollMobile desktopCols="md:grid-cols-2 lg:grid-cols-4">
            {[
              { name: 'LLC Formation', description: 'Protect your personal assets with limited liability', link: '/services/llc' },
              { name: 'Trademark Registration', description: 'Secure exclusive rights to your brand identity', link: '/services/trademark' },
              { name: 'Last Will & Testament', description: 'Ensure your wishes are honored and family protected', link: '/services/will' },
              { name: 'DBA Name', description: 'Operate your business under a different name', link: '/services/dba' },
              { name: 'Business License', description: 'Get all required licenses for your business', link: '/services/licenses' },
              { name: 'Registered Agent', description: 'Professional handling of legal documents', link: '/services/registered-agent' },
              { name: 'Living Trust', description: 'Avoid probate and protect your estate', link: '/services/trust' },
              { name: 'Copyright Registration', description: 'Protect your original creative works', link: '/services/copyright' },
            ].map((service, idx) => (
              <Link
                key={idx}
                to={service.link}
                className="group p-6 border border-gray-200 rounded-lg hover:shadow-2xl hover:border-blue-500 transition-all duration-300 transform hover:-translate-y-2"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">
                  {service.name}
                </h3>
                <p className="text-base text-gray-600 mb-3">{service.description}</p>
                <div className="flex items-center text-blue-600 text-sm font-semibold">
                  Learn more <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </HorizontalScrollMobile>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Content */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Why choose Legal Flick?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We combine technology, expertise, and customer service to deliver exceptional legal solutions that meet your needs.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Trusted by thousands</h3>
                    <p className="text-base text-gray-600">
                      Join thousands of satisfied customers who trust us with their legal needs. We've successfully supported countless legal milestones.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Attorney-backed services</h3>
                    <p className="text-base text-gray-600">
                      Every service is backed by legal professionals with an average of 17 years of experience ensuring accuracy and compliance.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <Star className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Satisfaction guarantee</h3>
                    <p className="text-base text-gray-600">
                      If you're not satisfied with our service, we'll refund your money. No questions asked. Your satisfaction is our priority.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Image */}
            <div>
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978"
                alt="Why Choose Legal Flick"
                className="rounded-lg shadow-xl w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Latest from our blog
            </h2>
            <p className="text-xl text-gray-600">
              Expert insights and guides to help you navigate legal matters
            </p>
          </div>

          <HorizontalScrollMobile desktopCols="md:grid-cols-3">
            {[
              {
                title: 'Understanding LLC Formation: A Complete Guide',
                description: 'Learn everything you need to know about forming an LLC, from benefits to step-by-step process',
                image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85',
                link: '/blog/llc-formation-guide',
              },
              {
                title: 'Estate Planning Essentials: Protecting Your Family\'s Future',
                description: 'Discover the essential documents and strategies for comprehensive estate planning',
                image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40',
                link: '/blog/estate-planning-essentials',
              },
              {
                title: 'Trademark Registration: Why Your Brand Needs Protection',
                description: 'Understand the importance of trademark protection and how to register your brand',
                image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72',
                link: '/blog/trademark-protection',
              },
            ].map((post, idx) => (
              <Card 
                key={idx} 
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group cursor-pointer"
                onClick={() => window.location.href = post.link}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{post.title}</h3>
                  <p className="text-gray-600 mb-4">{post.description}</p>
                  <div className="flex items-center text-blue-600 font-semibold">
                    Read article <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </HorizontalScrollMobile>
        </div>
      </section>

      {/* Strong CTA Banner */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Get Your Legal Documents Done Right Today
            </h2>
            <p className="text-xl lg:text-2xl text-blue-100 mb-10 leading-relaxed">
              Join thousands of satisfied customers who have trusted Legal Flick with their legal needs. 
              Take the first step toward protecting what matters most to you.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6 font-semibold shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
                onClick={() => setIsModalOpen(true)}
              >
                Get Started Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8 py-6 font-semibold shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
                onClick={openChat}
              >
                  Chat Now
              </Button>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-white">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-blue-200" />
                <span className="text-lg">100% Satisfaction Guarantee</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-blue-200" />
                <span className="text-lg">Trusted by 500,000+ Customers</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default HomePage;
